/**
 * ChangeHistoryDrawer — version history + restore for an admin-edited entity
 * (leverage grids, rules). Ported from digital_sizer's ChangeHistoryDrawer onto
 * eastviewos: same /admin/audit-log read + /restore write, but the heavy
 * react-diff-viewer dependency is replaced with a clean side-by-side before/after
 * JSON view, styled to the eastviewos aesthetic.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RestoreRounded from "@mui/icons-material/RestoreRounded";
import OpenInFullRounded from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRounded from "@mui/icons-material/CloseFullscreenRounded";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { adminPricingApi } from "../../api/adminPricingApi";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { teal } from "../../theme/colors";

const ACTION_COLOR = {
  create: { bg: "#ecfdf5", fg: "#047857" },
  update: { bg: teal[100], fg: teal[700] },
  delete: { bg: "#fef2f2", fg: "#dc2626" },
  restore: { bg: "#fffbeb", fg: "#b45309" },
  pin: { bg: "#fffbeb", fg: "#b45309" },
  unpin: { bg: "#f1f5f9", fg: "#64748b" },
};

const shortDate = (s) => {
  if (!s) return "";
  // created_at is a timestamp → render via formatDateTime (UTC→Eastern).
  // formatDateTime returns "" on invalid input, so fall back to the raw string.
  return formatDateTime(s, "datetime") || s;
};

const entryId = (e) => e.id || `${e.entity_id}-${e.version}`;
const asJson = (v) => (v == null ? "" : JSON.stringify(v, null, 2));

function JsonPane({ title, value }) {
  return (
    <Flx column sx={{ flex: 1, minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#94a3b8",
          mb: 0.5,
        }}
      >
        {title}
      </Txt>
      <Box
        component="pre"
        sx={{
          flex: 1,
          m: 0,
          p: 1.25,
          border: "1px solid #e5e7eb",
          borderRadius: 1.5,
          bgcolor: "#f8fafc",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 11.5,
          lineHeight: 1.5,
          color: "#334155",
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Box>
    </Flx>
  );
}

export default function ChangeHistoryDrawer({
  open,
  onClose,
  entityType,
  entityId: entityIdProp,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    adminPricingApi
      .getAuditLog(entityType, entityIdProp)
      .then((rows) => setData(Array.isArray(rows) ? rows : []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [open, entityType, entityIdProp]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !data.some((e) => entryId(e) === selectedId)) {
      setSelectedId(entryId(data[0]));
    }
  }, [data, selectedId]);

  const selected = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data.find((e) => entryId(e) === selectedId) || data[0];
  }, [data, selectedId]);

  const onRestore = useCallback(() => {
    if (!selected || selected.version == null) return;
    if (
      !window.confirm(
        `Restore ${entityType} ${entityIdProp} to v${selected.version}? A new audit entry will be created.`,
      )
    ) {
      return;
    }
    setRestoring(true);
    adminPricingApi
      .restoreVersion(entityType, entityIdProp, selected.version)
      .then(() => load())
      .catch((e) =>
        window.alert(`Restore failed: ${e?.response?.data?.detail || e?.message || e}`),
      )
      .finally(() => setRestoring(false));
  }, [selected, entityType, entityIdProp, load]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: expanded ? "100vw" : { xs: "100vw", md: 880 },
          transition: "width 0.2s ease",
        },
      }}
    >
      <Flx
        ac
        jb
        sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #d8dee4", flex: "0 0 auto" }}
      >
        <Box>
          <Txt sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            Change history
          </Txt>
          <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
            {entityType} · {entityIdProp}
          </Txt>
        </Box>
        <Flx ac g={0.5}>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Collapse" : "Expand to full width"}
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          >
            {expanded ? (
              <CloseFullscreenRounded sx={{ fontSize: 16 }} />
            ) : (
              <OpenInFullRounded sx={{ fontSize: 16 }} />
            )}
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Flx>

      <Flx sx={{ flex: 1, minHeight: 0 }}>
        {/* Entry list */}
        <Box
          sx={{
            width: 300,
            flexShrink: 0,
            borderRight: "1px solid #eef2f5",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Flx center sx={{ py: 5 }}>
              <CircularProgress size={20} sx={{ color: teal[700] }} />
            </Flx>
          ) : data.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Txt sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                No history yet — make a change and it will appear here.
              </Txt>
            </Box>
          ) : (
            data.map((e) => {
              const id = entryId(e);
              const sel = id === selectedId;
              const ac = ACTION_COLOR[e.action] || ACTION_COLOR.unpin;
              return (
                <Box
                  key={id}
                  onClick={() => setSelectedId(id)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                    borderLeft: "3px solid",
                    borderLeftColor: sel ? teal[700] : "transparent",
                    bgcolor: sel ? "#f1f6f9" : "transparent",
                    "&:hover": { bgcolor: sel ? "#f1f6f9" : "#f8fafc" },
                  }}
                >
                  <Flx ac g={0.75} sx={{ mb: 0.25 }}>
                    <Chip
                      size="small"
                      label={e.action}
                      sx={{
                        height: 17,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: ac.bg,
                        color: ac.fg,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                    {e.version != null && (
                      <Txt sx={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>
                        v{e.version}
                      </Txt>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Txt sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                      {shortDate(e.created_at)}
                    </Txt>
                  </Flx>
                  <Txt
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "#0f172a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {e.commit_message || "(no message)"}
                  </Txt>
                  <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
                    {e.created_by?.name || e.created_by?.email || "—"}
                  </Txt>
                </Box>
              );
            })
          )}
        </Box>

        {/* Detail / diff */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: 2 }}>
          {selected ? (
            <Flx column sx={{ height: "100%", minHeight: 0 }}>
              <Flx ac g={1} sx={{ mb: 1 }}>
                {selected.version != null && (
                  <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    v{selected.version}
                  </Txt>
                )}
                {selected.parent_version != null && (
                  <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
                    (parent v{selected.parent_version})
                  </Txt>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestoreRounded sx={{ fontSize: 16 }} />}
                  disabled={restoring || selected.version == null}
                  onClick={onRestore}
                  sx={{ textTransform: "none" }}
                >
                  {restoring ? "Restoring…" : "Restore"}
                </Button>
              </Flx>
              <Txt sx={{ fontSize: 12.5, color: "#475569", mb: 0.25 }}>
                {selected.commit_message || "(no commit message)"}
              </Txt>
              <Txt sx={{ fontSize: 11, color: "#94a3b8", mb: 1.5 }}>
                by {selected.created_by?.name || selected.created_by?.email || "—"} ·{" "}
                {shortDate(selected.created_at)}
              </Txt>
              <Flx g={1.5} sx={{ flex: 1, minHeight: 0 }}>
                <JsonPane
                  title={selected.parent_version ? `v${selected.parent_version}` : "Before"}
                  value={asJson(selected.before)}
                />
                <JsonPane title={`v${selected.version} (after)`} value={asJson(selected.after)} />
              </Flx>
            </Flx>
          ) : (
            <Txt sx={{ fontSize: 12.5, color: "#94a3b8" }}>
              Select a change on the left to see its diff.
            </Txt>
          )}
        </Box>
      </Flx>
    </Drawer>
  );
}
